import React, { useEffect, useMemo, useState } from 'react';
import { Button, Modal, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { Infinity as InfinityIcon } from 'react-bootstrap-icons';
import { useTheme } from '../../contexts/ThemeContext';
import ThemedSelect from '../shared/ThemedSelect';
import {
  getDefaultTimeInForce,
  resolveTimeInForceForOrderType,
  supportsExtendedHoursTimeInForce,
} from '../../utils/marketHours';

const asPrice = (value) => {
  const parsed = parseFloat(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
};

// Labels match the order-entry Time in Force dropdown.
const TIME_IN_FORCE_OPTIONS = [
  { value: 'DAY', label: 'Day' },
  { value: 'DYP', label: 'Day Ext. Hours' },
  { value: 'GTC', label: 'GTC' },
  { value: 'GCP', label: 'GTC Ext. Hours' },
];

export default function PositionSizeOrderModal({
  show,
  action,
  position,
  lastPrice,
  hasProtectiveOrders = false,
  submitting = false,
  onHide,
  onSubmit,
}) {
  const { colors, isLight } = useTheme();
  const fullQuantity = Math.abs(parseFloat(position?.Quantity) || 0);
  const currentPrice = asPrice(lastPrice);
  const isReduce = action === 'reduce';
  const [orderType, setOrderType] = useState('market');
  const [quantity, setQuantity] = useState(
    () => String(isReduce ? Math.max(1, Math.floor(fullQuantity / 2)) : 1),
  );
  const [limitPrice, setLimitPrice] = useState(() => (
    currentPrice != null ? String(currentPrice) : ''
  ));
  const [limitSyncEnabled, setLimitSyncEnabled] = useState(true);
  const [timeInForceOverride, setTimeInForceOverride] = useState(null);
  const isFuture = position?.AssetType?.toUpperCase?.() === 'FUTURE';
  const unit = isFuture
    ? (fullQuantity === 1 ? 'contract' : 'contracts')
    : (fullQuantity === 1 ? 'share' : 'shares');
  const numericQuantity = Number(quantity);
  const parsedQuantity = Math.floor(numericQuantity);
  // Reducing has a hard ceiling, so it gets a slider like the partial-close modal.
  // Adding has no natural maximum, so it stays a plain number field.
  const showQuantitySlider = isReduce && fullQuantity > 1;
  const sliderQuantity = Math.min(fullQuantity, Math.max(1, parsedQuantity || 1));
  const sliderPct = fullQuantity > 0 ? Math.round((sliderQuantity / fullQuantity) * 100) : 0;
  const parsedLimitPrice = asPrice(limitPrice);
  const validQuantity = Number.isInteger(numericQuantity)
    && parsedQuantity > 0
    && (!isReduce || parsedQuantity <= fullQuantity);
  const canSubmit = validQuantity && (orderType === 'market' || parsedLimitPrice != null);

  // While syncing, the limit price tracks the streaming last price.
  useEffect(() => {
    if (limitSyncEnabled && currentPrice != null) setLimitPrice(String(currentPrice));
  }, [limitSyncEnabled, currentPrice]);

  // Futures accounts have no extended-hours durations.
  const allowExtendedHours = !isFuture;
  const autoTimeInForce = getDefaultTimeInForce(orderType, { allowExtendedHours });
  // An override still gets coerced: market orders can't carry DYP / GCP.
  const timeInForce = timeInForceOverride
    ? resolveTimeInForceForOrderType(orderType, timeInForceOverride)
    : autoTimeInForce;
  const timeInForceOptions = useMemo(() => {
    const extendedAllowed = allowExtendedHours && supportsExtendedHoursTimeInForce(orderType);
    return TIME_IN_FORCE_OPTIONS.filter((option) => (
      extendedAllowed || (option.value !== 'DYP' && option.value !== 'GCP')
    ));
  }, [allowExtendedHours, orderType]);
  const timeInForceHint = useMemo(() => {
    if (timeInForceOverride) return null;
    if (autoTimeInForce === 'DYP') return 'Regular session is closed — set to run through extended hours.';
    if (autoTimeInForce === 'GCP') return 'Market is closed for the weekend — rests until the next extended session.';
    if (autoTimeInForce === 'GTC') return 'Market is closed — rests until the next session.';
    return null;
  }, [autoTimeInForce, timeInForceOverride]);

  const estimatedValue = useMemo(() => {
    const price = orderType === 'limit' ? parsedLimitPrice : currentPrice;
    return price != null && validQuantity ? price * parsedQuantity : null;
  }, [currentPrice, orderType, parsedLimitPrice, parsedQuantity, validQuantity]);

  const selectStyle = (selected) => ({
    flex: 1,
    height: 36,
    border: 'none',
    borderRadius: 7,
    backgroundColor: selected ? colors.primary : 'transparent',
    color: selected ? '#fff' : colors.textMuted,
    fontSize: 13,
    fontWeight: 700,
  });

  const inputStyle = {
    width: '100%',
    height: 40,
    borderRadius: 8,
    border: `1px solid ${colors.border}`,
    backgroundColor: colors.bgSecondary,
    color: colors.text,
    padding: '0 12px',
    fontVariantNumeric: 'tabular-nums',
  };

  const submitLabel = `${isReduce ? 'Reduce' : 'Add'} ${validQuantity ? parsedQuantity : ''}`.trim();

  return (
    <Modal show={show} onHide={onHide} centered backdrop="static">
      <Modal.Header
        closeButton
        closeVariant={isLight ? undefined : 'white'}
        style={{ backgroundColor: colors.bgPrimary, borderBottom: `1px solid ${colors.border}`, color: colors.text }}
      >
        <Modal.Title style={{ fontSize: 17, fontWeight: 700 }}>
          {isReduce ? 'Reduce' : 'Add to'} {position?.Symbol || 'position'}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ backgroundColor: colors.bgPrimary, color: colors.text }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, fontSize: 13 }}>
            <span style={{ color: colors.textMuted }}>
              {position?.LongShort || 'Position'} · {fullQuantity} {unit} open
            </span>
            <span style={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
              Last {currentPrice != null ? `$${currentPrice.toFixed(2)}` : '—'}
            </span>
          </div>

          <div
            role="group"
            aria-label="Order type"
            style={{ display: 'flex', gap: 3, padding: 3, borderRadius: 10, backgroundColor: colors.bgSecondary }}
          >
            <button type="button" aria-pressed={orderType === 'market'} onClick={() => setOrderType('market')} style={selectStyle(orderType === 'market')}>
              Market
            </button>
            <button type="button" aria-pressed={orderType === 'limit'} onClick={() => setOrderType('limit')} style={selectStyle(orderType === 'limit')}>
              Limit
            </button>
          </div>

          <label style={{ display: 'flex', flexDirection: 'column', gap: 7, fontSize: 12, fontWeight: 700, color: colors.textMuted }}>
            Quantity
            <input
              type="number"
              min="1"
              max={isReduce ? fullQuantity : undefined}
              step="1"
              value={quantity}
              onChange={(event) => setQuantity(event.target.value)}
              style={inputStyle}
            />
            {isReduce && !validQuantity && quantity !== '' ? (
              <span style={{ color: colors.danger, fontWeight: 600 }}>
                Enter a whole quantity from 1 to {fullQuantity}.
              </span>
            ) : null}
          </label>

          {showQuantitySlider ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: -8 }}>
              <input
                type="range"
                min={1}
                max={fullQuantity}
                step={1}
                value={sliderQuantity}
                onChange={(event) => setQuantity(event.target.value)}
                aria-label={`${isFuture ? 'Contracts' : 'Shares'} to reduce`}
                style={{ width: '100%', accentColor: colors.primary }}
              />
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  gap: 8,
                  fontSize: 11,
                  fontWeight: 600,
                  color: colors.textMuted,
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                <span>1</span>
                <span>{sliderPct}% of position</span>
                <span>All {fullQuantity}</span>
              </div>
            </div>
          ) : null}

          {orderType === 'limit' ? (
            <label style={{ display: 'flex', flexDirection: 'column', gap: 7, fontSize: 12, fontWeight: 700, color: colors.textMuted }}>
              Limit price
              <div style={{ position: 'relative' }}>
                <span
                  style={{
                    position: 'absolute',
                    left: 12,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    color: colors.text,
                    pointerEvents: 'none',
                    zIndex: 1,
                  }}
                >
                  $
                </span>
                <input
                  type="number"
                  min="0"
                  step="any"
                  value={limitPrice}
                  onChange={(event) => {
                    setLimitSyncEnabled(false);
                    setLimitPrice(event.target.value);
                  }}
                  style={{
                    ...inputStyle,
                    border: `1px solid ${limitSyncEnabled ? colors.primary : colors.border}`,
                    padding: '0 34px 0 24px',
                  }}
                  aria-label="Limit price"
                />
                <OverlayTrigger
                  placement="top"
                  overlay={(
                    <Tooltip>
                      {limitSyncEnabled ? 'Syncing to last price' : 'Sync limit price to last price'}
                    </Tooltip>
                  )}
                >
                  <span
                    role="button"
                    aria-label="Sync limit price to last price"
                    aria-pressed={limitSyncEnabled}
                    onClick={() => currentPrice != null && setLimitSyncEnabled(!limitSyncEnabled)}
                    style={{
                      position: 'absolute',
                      right: 10,
                      top: '50%',
                      transform: 'translateY(-50%)',
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: 20,
                      height: 20,
                      padding: 0,
                      border: 'none',
                      background: 'transparent',
                      color: limitSyncEnabled ? colors.primary : colors.textLight,
                      cursor: currentPrice == null ? 'default' : 'pointer',
                    }}
                  >
                    <InfinityIcon size={13} />
                  </span>
                </OverlayTrigger>
              </div>
            </label>
          ) : (
            <div style={{ fontSize: 12, color: colors.textMuted, lineHeight: 1.45 }}>
              A market order seeks the best available price. The displayed last price is an estimate, not a guaranteed fill.
            </div>
          )}

          <label style={{ display: 'flex', flexDirection: 'column', gap: 7, fontSize: 12, fontWeight: 700, color: colors.textMuted }}>
            Time in force
            <ThemedSelect
              aria-label="Time in force"
              value={timeInForce}
              options={timeInForceOptions}
              onChange={setTimeInForceOverride}
              width="100%"
              height={40}
              fontSize="0.875rem"
              borderColor={colors.border}
            />
            {timeInForceHint ? (
              <span style={{ fontWeight: 400, lineHeight: 1.45 }}>{timeInForceHint}</span>
            ) : null}
          </label>

          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              gap: 12,
              paddingTop: 14,
              borderTop: `1px solid ${colors.border}`,
              fontSize: 13,
            }}
          >
            <span style={{ color: colors.textMuted }}>Estimated value</span>
            <strong style={{ fontVariantNumeric: 'tabular-nums' }}>
              {estimatedValue != null
                ? estimatedValue.toLocaleString('en-US', { style: 'currency', currency: 'USD' })
                : '—'}
            </strong>
          </div>

          {isReduce && hasProtectiveOrders ? (
            <div
              style={{
                border: `1px solid ${colors.warning || '#d7a22a'}`,
                borderRadius: 8,
                padding: '10px 12px',
                fontSize: 12,
                lineHeight: 1.45,
                backgroundColor: isLight ? 'rgba(184, 124, 0, 0.08)' : 'rgba(255, 193, 7, 0.08)',
              }}
            >
              Open orders, including SL/TP protection, will be canceled before this reduction is submitted. Recreate protection for the remaining position after the order fills.
            </div>
          ) : null}
        </div>
      </Modal.Body>
      <Modal.Footer style={{ backgroundColor: colors.bgPrimary, borderTop: `1px solid ${colors.border}` }}>
        <Button variant="outline-secondary" onClick={onHide} disabled={submitting}>
          Cancel
        </Button>
        <Button
          variant={isReduce ? 'danger' : 'primary'}
          onClick={() => onSubmit?.({
            quantity: parsedQuantity,
            orderType,
            limitPrice: orderType === 'limit' ? parsedLimitPrice : null,
            timeInForce,
          })}
          disabled={!canSubmit || submitting}
        >
          {submitting ? 'Submitting…' : submitLabel}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
